import React from 'react';
import { Box } from '@material-ui/core';
import { IEntity } from 'shared/models/Entity';
import { EntryType } from 'shared/models/sheet/Sheet';
import FilterAndActionControls from 'shared/components/sheetsSubmitted/filterAndActionControls/FilterAndActionControls';
import { useSheetsSubmittedViewStyles } from 'modules/clients/content/TimeAndExpensePage/SheetsSubmitted/SheetsSubmittedView/SheetsSubmittedPageStyles';
import { useSubmittedTableStyles } from 'shared/components/sheetsSubmitted/sheetsSubmittedStyles';
import GridTable from 'shared/components/table/GridTable/GridTable';
import { ICellInfo } from 'shared/components/table/GridTable/GridTableModel';
import { pluralize } from 'shared/utils/formatters/pluralize';
import { useSelector } from 'react-redux';
import {
    selectEmployeeSubmittedSheetsFiltersIsNotEmpty,
} from 'store/components/employeeSubmittedSheets/employeeSubmittedSheetsSelectors';
import { IAvailableActionsProps } from 'modules/clients/content/TimeAndExpensePage/SheetsSubmitted/sheetsData';

interface ISheetsSubmittedDesktopViewProps<Row> {
    timeCells: Array<ICellInfo<Row>>;
    expenseCells: Array<ICellInfo<Row>>;
    timeRows: Array<Row>;
    expenseRows: Array<Row>;
    isLoading: boolean;
    showFilters?: boolean;
    sheetsEntryTypes?: Record<string, EntryType>;
    availableActions?: (props: IAvailableActionsProps) => JSX.Element;
}

const getTitle = (hasFilters: boolean, amount: number) => hasFilters
    ? `${amount} ${pluralize('result', amount)}.` : undefined;

export default function SheetsSubmittedDesktopView<Row extends IEntity>({
    timeCells, expenseCells, timeRows, expenseRows, isLoading, showFilters, sheetsEntryTypes, availableActions,
}: ISheetsSubmittedDesktopViewProps<Row>) {
    const classes = useSheetsSubmittedViewStyles();
    const tableClasses = useSubmittedTableStyles();

    const filtersNotEmpty = useSelector(selectEmployeeSubmittedSheetsFiltersIsNotEmpty);
    const hasFilters: boolean = filtersNotEmpty && (showFilters ?? true);

    const timeTitle = getTitle(hasFilters, timeRows.length);
    const expenseTitle = getTitle(hasFilters, expenseRows.length);

    return (
        <>
            <Box className={classes.mainContainer}>
                <FilterAndActionControls
                    showFilters={showFilters ?? true}
                    sheetType={EntryType.TIME}
                    title={timeTitle}
                    sheetsEntryTypes={sheetsEntryTypes}
                    availableActions={availableActions}
                />
                <Box className={tableClasses.tableMain}>
                    <GridTable rowData={timeRows}
                        headerCellClassName={tableClasses.headCell}
                        bodyCellClassName={tableClasses.bodyCell}
                        getKey={row => row.id}
                        cells={timeCells}
                        isLoading={isLoading}
                        stickyHeader
                    />
                </Box>
            </Box>
            <Box className={classes.mainContainer} mb={4}>
                <FilterAndActionControls
                    showFilters={false}
                    sheetType={EntryType.EXPENSE}
                    title={expenseTitle}
                    sheetsEntryTypes={sheetsEntryTypes}
                    availableActions={availableActions}
                />
                <Box className={tableClasses.tableMain}>
                    <GridTable rowData={expenseRows}
                        headerCellClassName={tableClasses.headCell}
                        bodyCellClassName={tableClasses.bodyCell}
                        getKey={row => row.id}
                        cells={expenseCells}
                        isLoading={isLoading}
                        stickyHeader
                    />
                </Box>
            </Box>
        </>
    );
}
